import React, { Component } from 'react'
import { connect } from 'react-redux'
import { NavLink, Redirect, Switch, Route, Link, withRouter } from 'react-router-dom'
import CustomButton from '../shared/CustomButton'
import Modal from '../shared/Modal'
import InvoiceRoute from './InvoiceRoute'
import InvoiceItemContainer from './InvoiceItemContainer'
import InvoiceForm from './InvoiceForm'
import InvoiceSearch from './InvoiceSearch'
import { EditPencilLine01, Trash } from '../normalizer'
import { InvoiceService } from '../../../services'
import { startFetch, endFetch } from '../../../actions/generalActions'
import { prefix } from '../../../i18n'
import ToastService from '../shared/Toast/ToastService'
import { CREATED_COLUMNS, TO_APPROVE_COLUMNS, REGISTERED_COLUMNS } from './invoiceColumns'
import { toInvoiceDetailPayload } from './invoiceProducts'

const tInvoice = prefix('invoice')

class UserContainer extends Component {
  state = {
    createdInvoices: [],
    toApproveInvoices: [],
    registeredInvoices: [],
    createdPage: 1,
    createdTotalPages: null,
    toApprovePage: 1,
    toApproveTotalPages: null,
    registeredPage: 1,
    registeredTotalPages: null,
    errors: {},
    saleTypes: [],
    filters: {},
    deleteModal: false,
    invoiceToDelete: null,
    deletePending: false,
  }

  componentDidMount() {
    const { campaign } = this.props
    this.fetchCreated(campaign, 1)
    this.fetchToApprove(campaign, 1)
    this.fetchRegistered(campaign, 1)
    this.fetchSaleTypes(campaign)
  }

  // Every navigation refetches the newly active list, with the filters
  // currently typed in the search bar (it sits above the tabs, so they follow
  // the user from one list to the next). Refetching unconditionally also keeps
  // a list current after an invoice moved between states — submitting a draft
  // redirects to /invoice/to_approve, whose copy was fetched on mount.
  componentDidUpdate(prevProps) {
    const { campaign, location } = this.props
    if (!location || !prevProps.location) return
    if (location.pathname === prevProps.location.pathname) return
    this.fetchActiveList(campaign, location.pathname, this.state.filters)
  }

  // Only the three list paths are filterable; detail pages are ignored.
  fetchActiveList = (campaign, pathname, filters) => {
    if (/^\/invoice\/to_approve\/?$/.test(pathname))      this.fetchToApprove(campaign, 1, filters)
    else if (/^\/invoice\/registered\/?$/.test(pathname)) this.fetchRegistered(campaign, 1, filters)
    else if (/^\/invoice(\/created)?\/?$/.test(pathname)) this.fetchCreated(campaign, 1, filters)
  }

  fetchCreated = (campaign, page, filters = this.state.filters) => {
    const { client } = this.props
    this.props.startFetch()
    InvoiceService.getCreatedInvoices({ clientId: client.slug, campaignId: campaign.slug, filters, page })
      .then(response => {
        // The index_* endpoints return the page metadata under `pagination`,
        // as read in DefaultTemplate.
        const { invoices, pagination } = response.data
        this.setState({
          createdInvoices: invoices,
          createdTotalPages: pagination ? pagination.total_pages : null,
          createdPage: page,
        })
        this.props.endFetch()
      })
      .catch(() => this.props.endFetch())
  }

  fetchToApprove = (campaign, page, filters = this.state.filters) => {
    const { client } = this.props
    this.props.startFetch()
    InvoiceService.getToApproveInvoices({ clientId: client.slug, campaignId: campaign.slug, filters, page })
      .then(response => {
        const { invoices, pagination } = response.data
        this.setState({
          toApproveInvoices: invoices,
          toApproveTotalPages: pagination ? pagination.total_pages : null,
          toApprovePage: page,
        })
        this.props.endFetch()
      })
      .catch(() => this.props.endFetch())
  }

  fetchRegistered = (campaign, page, filters = this.state.filters) => {
    const { client } = this.props
    this.props.startFetch()
    // Service method is "getRegistredInvoices" (original typo preserved in the service).
    InvoiceService.getRegistredInvoices({ clientId: client.slug, campaignId: campaign.slug, filters, page })
      .then(response => {
        const { invoices, pagination } = response.data
        this.setState({
          registeredInvoices: invoices,
          registeredTotalPages: pagination ? pagination.total_pages : null,
          registeredPage: page,
        })
        this.props.endFetch()
      })
      .catch(() => this.props.endFetch())
  }

  fetchSaleTypes = (campaign) => {
    const { client } = this.props
    InvoiceService.getSaleTypes({ clientId: client.slug, campaignId: campaign.slug })
      .then(response => this.setState({ saleTypes: response.data.sale_types || [] }))
      .catch(() => {})
  }

  // There is no bulk endpoint for products: each one is POSTed to
  // /invoices/:id/details in sequence, so the first failure stops the chain
  // and can be reported against an invoice that already exists.
  createInvoiceProducts = (invoiceId, products) => {
    const { campaign, client } = this.props
    return products.reduce((chain, product) => chain.then(() => {
      const { productId, clientCategoryId, quantity, unitPrice } = toInvoiceDetailPayload(product)
      return InvoiceService.addInvoiceProduct({
        clientId: client.slug,
        campaignId: campaign.slug,
        id: invoiceId,
        productId,
        clientCategoryId,
        quantity,
        unitPrice,
      })
    }), Promise.resolve())
  }

  // `submitForApproval` distinguishes the two bottom actions of the new-invoice
  // form: "Guardar rascunho" stops once the invoice and its details exist,
  // while "Enviar fatura" additionally calls set_invoice_to_approval, which is
  // what moves it out of the draft list.
  handleInvoiceCreation = ({ number, invoiceDate, saleType, document, products, submitForApproval }) => {
    const { campaign, client } = this.props
    this.props.startFetch()
    InvoiceService.addInvoiceHeader({
      clientId: client.slug,
      campaignId: campaign.slug,
      number,
      invoiceDate,
      saleType,
      document: document || {},
      name: (document && document.name) || '',
    })
      .then(response => {
        const created = response.data.invoice || response.data.invoices
        if (!created || !created.id) {
          return Promise.reject(new Error('Resposta inesperada ao criar a fatura'))
        }
        return this.createInvoiceProducts(created.id, products || [])
          .then(() => created.id)
          // Tag the failure with the invoice that was already created, so the
          // user lands on its detail page instead of losing the whole form.
          .catch(error => Promise.reject(Object.assign(error || new Error('detail-failed'), { invoiceId: created.id })))
      })
      .then(invoiceId => {
        if (!submitForApproval) return invoiceId
        // A failure here leaves a valid draft behind, so it is reported without
        // discarding the invoice: the user can retry from its detail page.
        return InvoiceService.sendToValidation({
          clientId: client.slug,
          campaignId: campaign.slug,
          id: invoiceId,
        })
          .then(() => {
            this.fetchToApprove(campaign, 1)
            return null
          })
          .catch(() => {
            ToastService.error('A fatura foi guardada mas não foi possível enviá-la para aprovação')
            return invoiceId
          })
      })
      .then(draftInvoiceId => {
        this.fetchCreated(campaign, 1)
        this.props.endFetch()
        this.setState({ errors: {} })
        this.props.history.push(draftInvoiceId ? `/invoice/created/${draftInvoiceId}` : '/invoice/to_approve')
      })
      .catch(error => {
        this.props.endFetch()
        if (error && error.invoiceId) {
          this.fetchCreated(campaign, 1)
          ToastService.error('A fatura foi criada mas não foi possível guardar todos os produtos')
          this.props.history.push(`/invoice/created/${error.invoiceId}`)
          return
        }
        if (error && error.response && error.response.data) {
          this.setState({ errors: error.response.data })
        } else {
          ToastService.error('Erro ao criar a fatura')
        }
      })
  }

  // Each list has its own index_* endpoint, so the search applies to whichever
  // tab is open, as in DefaultTemplate where every tab filters its own list.
  handleSearchRequest = (filters) => {
    const { campaign, location } = this.props
    this.setState({ filters }, () => {
      this.fetchActiveList(campaign, location ? location.pathname : '/invoice/created', filters)
    })
  }

  handleClientPageUpdate = (path) => ({ selected }) => {
    const { campaign } = this.props
    const page = selected + 1
    if (path.includes('created'))     this.fetchCreated(campaign, page)
    if (path.includes('to_approve'))  this.fetchToApprove(campaign, page)
    if (path.includes('registered'))  this.fetchRegistered(campaign, page)
  }

  showDeleteModal = (invoice) => this.setState({ deleteModal: true, invoiceToDelete: invoice })
  hideDeleteModal = () => this.setState({ deleteModal: false, invoiceToDelete: null, deletePending: false })

  handleDeleteInvoice = () => {
    const { campaign, client } = this.props
    const { invoiceToDelete, createdPage } = this.state

    this.setState({ deletePending: true })
    InvoiceService.deleteInvoice({ clientId: client.slug, campaignId: campaign.slug, id: invoiceToDelete.id })
      .then(() => {
        this.hideDeleteModal()
        this.fetchCreated(campaign, createdPage)
      })
      .catch(() => {
        this.setState({ deletePending: false })
        ToastService.error('Erro ao eliminar a fatura')
      })
  }

  renderCreatedActions = (invoice) => (
    <div className="invoice__actions">
      <Link to={`/invoice/created/${invoice.id}`} className="invoice__action" title="Editar">
        <EditPencilLine01 className="invoice__action-icon" />
      </Link>
      <button
        type="button"
        className="invoice__action invoice__action--danger"
        title="Eliminar"
        onClick={() => this.showDeleteModal(invoice)}
      >
        <Trash className="invoice__action-icon" />
      </button>
    </div>
  )

  render() {
    const { campaign } = this.props
    const {
      createdInvoices, toApproveInvoices, registeredInvoices,
      createdPage, createdTotalPages,
      toApprovePage, toApproveTotalPages,
      registeredPage, registeredTotalPages,
      errors, saleTypes,
      deleteModal, deletePending,
    } = this.state

    const tabs = [
      { label: tInvoice('created'), path: '/invoice/created' },
      { label: tInvoice('to-approve'), path: '/invoice/to_approve' },
      { label: tInvoice('registered'), path: '/invoice/registered' },
    ]

    return (
      <>
        <Switch>
        {/* ── Inline new-invoice page ── */}
        <Route exact path="/invoice/new"
          render={() => (
            <InvoiceForm
              onSubmit={this.handleInvoiceCreation}
              campaign={campaign}
              client={this.props.client}
              errors={errors}
              saleTypes={saleTypes}
            />
          )}
        />

        {/* ── Invoice detail pages: no outer chrome ── */}
        <Route path="/invoice/created/:id" render={() => (
          <InvoiceItemContainer editable linking="/invoice/created" campaign={campaign} client={this.props.client} />
        )} />
        <Route path="/invoice/to_approve/:id" render={() => (
          <InvoiceItemContainer editable={false} linking="/invoice/to_approve" campaign={campaign} client={this.props.client} />
        )} />
        <Route path="/invoice/registered/:id" render={() => (
          <InvoiceItemContainer editable={false} linking="/invoice/registered" campaign={campaign} client={this.props.client} />
        )} />

        {/* ── List / detail views ── */}
        <Route render={() => (
          <div className="invoice-layout">
            <div className="invoice-layout-header">
              <h4 className="invoice-layout-header-title">{tInvoice('title')}</h4>
            </div>

            <nav className="invoice-layout-navigation">
              {tabs.map(tab => (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  activeClassName="invoice-layout-navigation-item-active"
                  className="invoice-layout-navigation-item"
                >
                  {tab.label}
                </NavLink>
              ))}
              <div className="invoice-layout-navigation-add">
                <NavLink to="/invoice/new" className="gl-button secondary medium body-m">
                  {tInvoice('add')}
                </NavLink>
              </div>
            </nav>

            <InvoiceSearch
              campaign={campaign}
              onSearchRequest={this.handleSearchRequest}
            />

            <div className="invoice-layout-content">
              {/* Bare /invoice and any path this view does not own (e.g. the
                  validator's /invoice/to_validate) fall back to the first tab,
                  so the tabs are never shown above an unmatched route. */}
              <Switch>
                <Route path="/invoice/created" />
                <Route path="/invoice/to_approve" />
                <Route path="/invoice/registered" />
                <Redirect to="/invoice/created" />
              </Switch>

              <InvoiceRoute
                path="/invoice/created"
                columns={CREATED_COLUMNS}
                renderActions={this.renderCreatedActions}
                invoices={createdInvoices}
                onPageChange={this.handleClientPageUpdate}
                currentPage={createdPage - 1}
                totalPages={createdTotalPages}
              />
              <InvoiceRoute
                path="/invoice/to_approve"
                columns={TO_APPROVE_COLUMNS}
                invoices={toApproveInvoices}
                onPageChange={this.handleClientPageUpdate}
                currentPage={toApprovePage - 1}
                totalPages={toApproveTotalPages}
              />
              <InvoiceRoute
                path="/invoice/registered"
                columns={REGISTERED_COLUMNS}
                invoices={registeredInvoices}
                onPageChange={this.handleClientPageUpdate}
                currentPage={registeredPage - 1}
                totalPages={registeredTotalPages}
              />
            </div>
          </div>
        )} />
      </Switch>

      {/* ── Delete invoice confirm modal (/invoice/created) ── */}
      <Modal small untoggle={this.hideDeleteModal} show={deleteModal}>
        <div className="gl2026-modal__header">
          <h4 className="gl2026-modal__title">Eliminar fatura?</h4>
        </div>
        <div className="gl2026-modal__footer">
          <CustomButton secondary type="button" onClick={this.hideDeleteModal}>Não</CustomButton>
          <CustomButton type="button" disabled={deletePending} onClick={this.handleDeleteInvoice}>Sim</CustomButton>
        </div>
      </Modal>
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  client: state.userReducer.client,
  campaignUser: state.balanceReducer.campaignUser,
})

const mapDispatchToProps = (dispatch) => ({
  startFetch: () => dispatch(startFetch()),
  endFetch: () => dispatch(endFetch()),
})

// withRouter MUST wrap connect() so that URL changes inject a new `location`
// ownProp, forcing connect()'s shouldComponentUpdate to return true and
// re-render the InvoiceRoute children against the updated location.
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(UserContainer))
